import { Module } from 'vuex'
import {
  create,
  gameplayCopy,
  gameplayDelete,
  gameplayList,
  getReplayDetail,
  saveReplay,
} from '@pt/api/vrecord/gameplay'
import _ from 'lodash'
import {
  GamePlayParamType,
  PackageFile,
  ReplayDetailResponseData,
} from '@pt/api/vrecord/gameplay.type'
import { projectFind } from '@pt/api/vrecord/project'
import { ProjectDetail } from '@pt/api/vrecord/project/index.type'
import { Message } from 'element-ui'
import { PTRootState } from '@pt/store/index.type'
import i18n from '@/lang'

type MouseInput = 'mouseMove' | 'mouseDown' | 'mouseUp'
export type MouseData = {
  mouseDown: boolean // 是否处于鼠标按下的状态
  mouseMove: boolean // 是否处于鼠标移动的状态
  mouseUp: boolean // 是否处于鼠标抬起的状态
  position: {
    x: number // 当前鼠标的 x 轴坐标
    y: number // 当前鼠标的 Y 轴坐标
  }
  stateInputData: MouseInput[]
}

export interface RecordParams {
  showCursor: number // 0: 不显示，1： 显示
  showCursorOnclick: number // 0: 一直显示，1： 点击显示
  cursorWidth: number // 光标宽度大小
  cursorHeight: number // 光标高度大小
  cursorUrl: string // 光标背景图
  cursorFilePath?: string // 光标背景图，上传之后的 file_path 地址，保存时需要
  cursorAssetId?: string // 光标背景图引用资产库的资源 id, 为什么要用 string， 因为 number 类型超过 17 位， JS 字符串解析就会失真
  cursorRotation: number // 光标旋转角度
}

interface RecordData {
  replay: MouseData[]
  duration: number
}

export enum RecordMode {
  playing,
  record,
  replay,
}

interface RecordGameplayInfo {
  id: number // 当前录屏项目的 gamePlay id，用于请求 gamePlay 行为数据
  name: string // 项目版本名称
  orientation: number // 1: 横屏; 2: 竖屏
  mode: number // 0: 试玩模式， 1: 录制模式， 2: 回放模式
  packageFiles: PackageFile[]
  unityVersion: string
  gameParams: GamePlayParamType[]
}

interface RecordProductInfo {
  id: number
  name: string
  iconUrl: string
  projectList: ProjectDetail[]
  projectId: number
  gamePlayList: any[]
}

interface PromptDialog {
  visible: boolean
  mainTitle: string
  subTitle: string
  type: number // 0 为普通提示，只要关闭即可，1 为保存提示，确认之后才保存，2 代表导出提示，确认之后才导出
}

export enum FrameStatus {
  loading,
  init,
  startRecord,
  stopRecord,
  startReplay,
  stopReplay,
}

export interface RecordState
  extends RecordParams,
    RecordGameplayInfo,
    RecordData {
  workFrame: null | HTMLIFrameElement
  product: RecordProductInfo
  workFrameStatus: FrameStatus
  loading: boolean
  saveLoading: boolean
  hasAddEventListener: boolean
  promptDialog: PromptDialog
}

const EVENT_LOAD_TYPE = 'RECORD:load'
const EVENT_INIT_PREFIX = 'RECORD:'

// 引用图片 '@/assets/playturbo/image_fail.png'
// 为什么要用 base64 编码，因为在 iframe 中引用图片，需要使用 base64 编码
const defaultImage =
  'data:image/png;base64,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'

const RecordModule: Module<RecordState, PTRootState> = {
  namespaced: true,

  state: {
    id: 0,
    name: '',
    showCursor: 1,
    showCursorOnclick: 0,
    cursorWidth: 20,
    cursorHeight: 20,
    cursorUrl: '',
    cursorFilePath: '',
    cursorAssetId: '0',
    mode: 0,
    workFrame: null,
    gameParams: [],
    orientation: 1,
    replay: [],
    duration: 0,
    packageFiles: [],
    unityVersion: '',
    cursorRotation: 0,
    product: {
      id: 0,
      name: '',
      iconUrl: '',
      projectList: [],
      projectId: 0,
      gamePlayList: [],
    },
    workFrameStatus: FrameStatus.loading,
    loading: true,
    saveLoading: false,
    hasAddEventListener: false,
    promptDialog: {
      visible: false,
      mainTitle: '',
      subTitle: '',
      type: 0,
    },
  },

  mutations: {
    setWorkFrame(state, frame: HTMLIFrameElement) {
      state.workFrame = frame
    },
    setReplayDetailToState(state, detailData: ReplayDetailResponseData) {
      state.name = detailData.name
      state.orientation = detailData.orientation
      state.duration = detailData.duration
      state.gameParams = detailData.params
      state.product.id = detailData.product.id
      state.product.iconUrl = detailData.product.icon_url
      state.product.name = detailData.product.name
      state.packageFiles = detailData.package_files
      state.unityVersion = detailData.unity_version
      state.replay = detailData.replay || []
      const cursorData = detailData.setting.cursor
      state.showCursor = cursorData.display_mode !== 0 ? 1 : 0
      state.showCursorOnclick = cursorData.display_mode === 1 ? 1 : 0
      state.cursorWidth = cursorData.width || 100
      state.cursorHeight = cursorData.height || 100
      state.cursorUrl = detailData.cursor_url
      state.cursorRotation = cursorData.rotation_angle
      state.mode = RecordMode.playing
      state.cursorAssetId = detailData.cursor_asset_id
    },
    setProjectList(state, list: ProjectDetail[]) {
      state.product.projectList = list
    },
    setGamePlayList(state, list: any[]) {
      state.product.gamePlayList = list || []
    },
    setGamePlayId(state, id: number) {
      state.id = id
    },
    setGamePlayName(state, name: string) {
      state.name = name
    },
    setGameMode(state, mode: number) {
      state.mode = mode
    },
    setProjectId(state, id: number) {
      state.product.projectId = id
    },
    setShowCursor(state, status: number) {
      state.showCursor = status
    },
    setCursorUrl(state, url: string) {
      state.cursorUrl = url
    },
    setCursorFile(state, filePath: string) {
      state.cursorFilePath = filePath
    },
    setCursorOnclickStatus(state, status: number) {
      state.showCursorOnclick = status
    },
    setCursorRotation(state, rotation: number) {
      state.cursorRotation = rotation
    },
    setCursorSize(state, data: { width: number; height: number }) {
      state.cursorWidth = data.width
      state.cursorHeight = data.height
    },
    setGameParams(state, data: { key: string; value: GamePlayParamType }) {
      const targetParams = state.gameParams.findIndex(
        (item) => String(item.id) === data.key,
      )
      if (targetParams !== -1) {
        state.gameParams[targetParams] = {
          ...state.gameParams[targetParams],
          value: JSON.stringify(data.value.value),
        }
      }
    },
    setReplay(state, data) {
      state.replay = data
    },
    setDuration(state, duration: number) {
      state.duration = duration
    },
    setFrameStatus(state, status: FrameStatus) {
      state.workFrameStatus = status
    },
    setLoading(state, loading: boolean) {
      state.loading = loading
    },
    setCursorAssetId(state, id: string) {
      state.cursorAssetId = String(id)
    },
    setPromptDialog(state, data: PromptDialog) {
      state.promptDialog = data
    },
    setSaveLoading(state, loading: boolean) {
      state.saveLoading = loading
    },
  },

  actions: {
    async getGameplayData({ state, commit, dispatch }, id?: number) {
      try {
        commit('setLoading', true)
        const gamePlayId = id || state.id
        const res = await getReplayDetail(gamePlayId)
        commit('setLoading', false)
        if (res.data.code === 0) {
          commit('setReplayDetailToState', _.get(res, 'data.data', {}))
          await dispatch('getProductProject', state.product.id)
          // 如果没有了光标图片，就提示用户重新上传
          if (state.cursorAssetId === '-1') {
            commit('setPromptDialog', {
              visible: true,
              mainTitle: i18n.t(
                'playTurbo.record.PromptMessage.assetNotFound.title',
              ),
              subTitle: i18n.t(
                'playTurbo.record.PromptMessage.assetNotFound.label',
              ),
              type: 0,
            })
            commit('setCursorUrl', defaultImage)
          }
        }
      } catch (e) {
        commit('setLoading', false)
        console.log('获取 gameplay 数据失败', e)
      }
    },

    async getProductProject({ commit }, id: number) {
      try {
        const res = await projectFind({
          product_id: id,
          status: 2,
          order: '-updated_time',
          page: 1,
          page_size: 100,
        })
        if (res.data.code === 0) {
          commit('setProjectList', _.get(res, 'data.data.list', []))
        }
      } catch (e) {
        console.log('获取产品项目数据失败', e)
      }
    },

    async getProjectGamePlay({ commit }, id: number) {
      try {
        const res = await gameplayList({
          pid: id,
          page: 1,
          page_size: 100,
        })
        if (res.data.code === 0) {
          commit('setGamePlayList', _.get(res, 'data.data.list', []))
        }
      } catch (e) {
        console.log('获取产品项目数据失败', e)
      }
    },

    async createGamePlay(
      { state, dispatch },
      {
        projectId,
        gamePlayName,
      }: {
        projectId?: number
        gamePlayName?: string
      },
    ) {
      try {
        const res = await create(
          projectId || state.product.projectId,
          gamePlayName,
        )
        if (res.data.code === 0) {
          await dispatch('getProjectGamePlay', state.product.projectId)
        } else {
          return Promise.reject(
            new Error(res.data.msg || 'createGamePlay 失败'),
          )
        }
      } catch (e: any) {
        const msg = e && e.data && e.data.msg
        return Promise.reject(new Error(msg || 'createGamePlay 失败'))
      }
    },

    async copyGamePlay(
      { state, dispatch },
      {
        id,
        name,
      }: {
        id?: number
        name?: string
      },
    ) {
      try {
        const res = await gameplayCopy(id || state.id, name)
        if (res.data.code === 0) {
          await dispatch('getProjectGamePlay', state.product.projectId)
        } else {
          return Promise.reject(new Error(res.data.msg || 'copyGamePlay 失败'))
        }
      } catch (e: any) {
        const msg = e && e.data && e.data.msg
        return Promise.reject(new Error(msg || 'copyGamePlay 失败'))
      }
    },

    async deleteGamePlay({ state, dispatch }, id: number) {
      try {
        const res = await gameplayDelete(id)
        if (res.data.code === 0) {
          await dispatch('getProjectGamePlay', state.product.projectId)
        }
      } catch (e) {
        console.log('删除 gamePlay 失败', e)
      }
    },

    async saveGameplayData({ state, commit, dispatch }, data) {
      try {
        let display_mode = 0
        // 一直显示
        if (state.showCursor) {
          if (state.showCursorOnclick === 0) {
            display_mode = 2
          } else {
            display_mode = 1
          }
        } else display_mode = 0
        const stateData: any = {
          cover: '',
          id: state.id,
          name: state.name,
          orientation: state.orientation,
          param_values: JSON.stringify(
            state.gameParams.map((item) => {
              return {
                id: item.id,
                value: item.value,
              }
            }),
          ),
          setting: JSON.stringify({
            cursor: {
              display_mode: display_mode,
              width: state.cursorWidth,
              height: state.cursorHeight,
              rotation_angle: state.cursorRotation,
            },
          }),
        }
        // 如果有 state.cursorFilePath
        if (state.cursorFilePath) {
          stateData.cursor_file = state.cursorFilePath
        }
        if (state.cursorAssetId) {
          stateData.cursor_asset_id = state.cursorAssetId
        }
        // 如果少于两秒，不允许保存
        if (data && typeof data.duration === 'number' && data.duration < 2000) {
          return Promise.reject(new Error('视频录制时长过短，请重新录制。'))
        }
        // 如果有传 data ,则以 data 为主，如果没有，则用 state 的内容赋值
        const res = await saveReplay(data || stateData)
        if (res.data.code === 0) {
          // 如果是页面中触发的保存，需要同步一下 replay 到 state
          if (data && data.replay) {
            commit('setReplay', JSON.parse(data.replay))
            commit('setDuration', data.duration)
          }
          return
        } else if (res.data.code === 600002) {
          // 600002 代表资源库的资源已经被删除
          Message({
            message: `${i18n.t('playTurbo.record.error.delete')}`,
            type: 'warning',
            duration: 5 * 1000,
            showClose: true,
          })
          commit('setCursorUrl', defaultImage)
          commit('setCursorAssetId', '-1')
          dispatch('postMessageToFrame', {
            type: 'settingUpdate',
          })
          return Promise.reject(new Error(res.data.msg))
        }
        return Promise.reject(new Error(res.data.msg || '保存失败'))
      } catch (e: any) {
        const msg = e && e.data && e.data.msg
        return Promise.reject(new Error(msg || '保存失败'))
      }
    },

    addListenerEvent({ state, commit, dispatch }) {
      if (state.hasAddEventListener) return
      state.hasAddEventListener = true
      window.addEventListener('message', async (event: MessageEvent) => {
        if (
          event.data.type &&
          event.data.type.indexOf(EVENT_INIT_PREFIX) === 0
        ) {
          const type = event.data.type.split(':')[1]
          switch (type) {
            case 'saveData': {
              const { replay, duration, cover } = event.data.data
              try {
                await dispatch('saveGameplayData', {
                  id: state.id,
                  duration,
                  replay,
                  cover,
                })
                Message({
                  message: `${i18n.t('playTurbo.record.success.msg')}`,
                  type: 'success',
                  duration: 5 * 1000,
                  showClose: true,
                })
              } catch (e: any) {
                if (e && e.message) {
                  Message({
                    message: e.message,
                    type: 'error',
                    duration: 5 * 1000,
                    showClose: true,
                  })
                }
              }
              break
            }
            default:
              if (FrameStatus[type]) {
                commit('setFrameStatus', FrameStatus[type])
              }
              break
          }
        }
      })
    },

    addLoadEventCallback(state, callBack: () => void) {
      window.addEventListener('message', (event: MessageEvent) => {
        if (event.data.type === EVENT_LOAD_TYPE) {
          callBack && callBack()
        }
      })
    },

    postMessageToFrame({ state, commit }, { type, config }) {
      if (state.workFrame && state.workFrame.contentWindow) {
        let data = {}
        switch (type) {
          case 'startRecording':
            commit('setGameMode', RecordMode.record)
            commit('setFrameStatus', FrameStatus.loading)
            break
          case 'saveRecording':
            commit('setGameMode', RecordMode.playing)
            commit('setFrameStatus', FrameStatus.stopRecord)
            break
          case 'initGame':
            data = {
              packageFiles: state.packageFiles,
              version: state.unityVersion,
              params: state.gameParams,
              recordData: state.replay,
              showCursor: state.showCursor,
              cursorSize: state.cursorWidth,
              showCursorOnclick: state.showCursorOnclick,
              cursorWidth: state.cursorWidth,
              cursorHeight: state.cursorHeight,
              cursorUrl: state.cursorUrl,
              cursorRotation: state.cursorRotation,
              mode: state.mode,
            }
            break
          case 'configUpdate':
            data = config
            break
          case 'settingUpdate':
            data = {
              showCursor: state.showCursor,
              showCursorOnclick: state.showCursorOnclick,
              cursorWidth: state.cursorWidth,
              cursorHeight: state.cursorHeight,
              cursorUrl: state.cursorUrl,
              cursorRotation: state.cursorRotation,
            }
            break
          case 'startReplay':
            data = {
              recordData: state.replay,
            }
            commit('setGameMode', RecordMode.replay)
            commit('setFrameStatus', FrameStatus.startReplay)
            break
          default:
            break
        }
        state.workFrame.contentWindow.postMessage({
          type,
          data,
        })
      } else {
        console.warn('还没注册 workFrame')
      }
    },

    reloadFrame({ state, commit, dispatch }, cb: () => void) {
      if (state.workFrame && state.workFrame.contentWindow) {
        state.workFrame.contentWindow.location.reload()
        commit('setFrameStatus', FrameStatus.loading)
        setTimeout(() => {
          dispatch('postMessageToFrame', {
            type: 'initGame',
          })
          if (cb) {
            cb()
          }
        }, 3000)
      }
    },
  },
}

export default RecordModule
